import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DatePipe } from '@angular/common';

import {
  BehaviorSubject,
  catchError,
  delay,
  finalize,
  map,
  Observable,
  tap,
  throwError,
} from 'rxjs';

import { WebApiService } from './web-api.service';
import { DomainTrackingService } from './domain-tracking.service';

import { CityInfo } from '../interfaces/city-info';
import { TequilaConfig } from '../configs/config';
import { TripWizardStateService } from '../../shared/components/trip-wizard/trip-wizard-state.service';

@Injectable({
  providedIn: 'root',
})
export class PackageService {
  public tequilaApiUrl: string = TequilaConfig.tequilaApiUrl;
  public tequilaApiKey: string = TequilaConfig.tequilaApiKey;
  public disableSearchButtonStatus = true;
  public tripFlights: any = null;
  public tripFlightsLoading: boolean = true;
  public autoCompleteStartResults = new EventEmitter<any>();
  private packageId = null;
  private packageDatesString: string[] = [];
  private stopPolling = false;
  private packageStatusById = {};
  private packageEventsByCityIdAndDates: Record<
    string,
    {
      id: string;
      imageUrl: string;
      url: string;
      name: string;
      currency: string;
      price: number;
    }
  > = {};
  private packageHotelsByCityAndDates: Record<string, any> = {};
  private tripFlightsSubject = new BehaviorSubject<any>(null);
  tripFlightsValue: Observable<any> = this.tripFlightsSubject.asObservable();
  packages$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  constructor(
    public http: HttpClient,
    public datePipe: DatePipe,
    private webApiService: WebApiService,
    private domainTrackingService: DomainTrackingService,
    private tripWizardStateService: TripWizardStateService,
  ) {}

  get currentPackageId(): string {
    return this.packageId;
  }

  get packageCityEventMap(): any {
    return this.packageEventsByCityIdAndDates;
  }

  get hotels() {
    return this.packageHotelsByCityAndDates;
  }

  get currentPackageDatesString(): string[] {
    return this.packageDatesString;
  }

  public createNewPackage(
    startDate: Date,
    budget: string,
    airport: string = '',
    callback?: (response: any) => void,
  ): void {
    this.tripFlightsLoading = true;
    const daysToAdd = 1;
    const selectedDate = new Date();
    selectedDate.setHours(0, 0, 0, 0);
    const nextDate = new Date(
      selectedDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000,
    );
    if (startDate < nextDate) {
      startDate = nextDate;
    }
    const arrivalDate = this.datePipe.transform(startDate, 'YYYY-MM-dd');
    let queryParams = new HttpParams()
      .set('currency', this.domainTrackingService.getCurrency())
      .set('budget', budget)
      .set('arrivalDate', arrivalDate)
      .set('pax', this.tripWizardStateService.currentTripPaxString);

    if (this.tripWizardStateService.currentTripPaxChildrenAgesString) {
      queryParams = queryParams.append(
        'paxChildrenAges',
        this.tripWizardStateService.currentTripPaxChildrenAgesString,
      );
    }

    if (airport && this.packageId) {
      queryParams = queryParams.append('key', this.packageId);
    }

    this.webApiService.get('package/create', queryParams).subscribe((res) => {
      if (res && res.packageId) {
        this.packageEventsByCityIdAndDates = {};
        this.packageId = res.packageId;
        this.stopPolling = false;
        let pollNumber = 0;

        const poll = () => {
          return this.startPollingRequests(
            res.packageId,
            pollNumber,
            airport,
          ).pipe(
            tap((resp) => {
              this.packageStatusById[res.packageId] = resp.status;

              if (
                resp.donePolling &&
                this.packageStatusById[res.packageId] === 'done'
              ) {
                this.stopPolling = true;

                if (resp.results && resp.results.length) {
                  this.packageDatesString = [];
                  resp.results.forEach((result: any) => {
                    const {
                      dates: { start, end },
                    } = result;
                    this.packageDatesString.push(`${start}-${end}`);
                  });
                }

                if (callback) {
                  const firstCity = resp.results[0];
                  const lastCity = resp.results[resp.results.length - 1];
                  this.getTripFlights({
                    cityId: firstCity.id,
                    returnCityId: lastCity.id,
                    dates: `${firstCity.dates.start}-${lastCity.dates.end}`,
                    airport: airport,
                  });
                  callback(resp);
                } else {
                  this.payloadAllDataByAllCities(resp, airport);
                }
              }
            }),
          );
        };

        const executePolling = () => {
          poll()
            .pipe(delay(1000))
            .subscribe({
              next: () => {
                pollNumber++;
                if (!this.stopPolling && pollNumber < 8) executePolling();
              },
              error: () => (this.stopPolling = true),
            });
        };
        executePolling();
      }
    });
  }

  public payloadAllDataByAllCities(resp: any, airport: string = ''): void {
    const firstCity = resp.results[0];
    const lastCity = resp.results[resp.results.length - 1];
    this.getTripFlights({
      cityId: firstCity.id,
      returnCityId: lastCity.id,
      dates: `${firstCity.dates.start}-${lastCity.dates.end}`,
      airport: airport,
    });
    this.getCitiesEvents(resp.results).then();
  }

  public startPollingRequests(
    packageId: string,
    pollNumber: number,
    airport: string = '',
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('key', packageId)
      .set('poll', pollNumber);

    if (airport) {
      queryParams = queryParams.set('airport', airport);
    }

    return this.webApiService.get('package/poll', queryParams);
  }

  public async getCitiesEvents(cityInfos: CityInfo[]): Promise<void> {
    for (const cityInfo of cityInfos) {
      await this.getViatorCityEvents(cityInfo);
    }
  }

  public getViatorEventId(body: CityInfo): string {
    return `${body.id}_${this.packageId}_${body.days}`;
  }

  public async getViatorCityEvents(body: CityInfo): Promise<any> {
    const queryParams = new HttpParams()
      .set('city', body.id)
      .set('dates', `${body.dates.start}-${body.dates.end}`)
      .set('key', this.packageId);
    const eventId = this.getViatorEventId(body);
    const events = this.packageEventsByCityIdAndDates[eventId];

    if (events || !this.packageId) {
      return Promise.resolve(events);
    } else {
      return new Promise((resolve, reject) => {
        const parseResponseBody = (res: any): void => {
          this.packageEventsByCityIdAndDates[eventId] =
            this.groupViatorEventsByCategories(res, 'name');
        };
        this.webApiService
          .get('events/all', queryParams)
          .pipe(
            map((eventsResp) => {
              if (eventsResp.status === 'success') {
                parseResponseBody(eventsResp);
              } else {
                parseResponseBody({ results: [] });
              }
              resolve(this.packageEventsByCityIdAndDates[eventId]);
            }),
            catchError((err) => {
              parseResponseBody({ results: [] });
              reject(err);
              return throwError(err);
            }),
          )
          .subscribe();
      });
    }
  }

  protected MAX_EVENTS = '30';
  public getViatorEventsByCityId(cityId: number | string): Observable<any> {
    const params = new HttpParams().set('city', cityId).set('limit', this.MAX_EVENTS);
    return this.webApiService.get('events/searchCity', params).pipe(
      map((res) => {
        if (res.status === 'success') {
          return this.groupViatorEventsByCategories(res, 'title');
        }
      }),
    );
  }

  getStartCityAutocomplete(value: string): void {
    const headers = {
      apikey: this.tequilaApiKey,
    };
    const params = new HttpParams().set('term', value);

    this.http
      .get(this.tequilaApiUrl + 'locations/query', { headers, params })
      .subscribe({
        next: (data: any) => {
          if (!data || !data.locations || data.locations.length == 0) {
            const results = [
              {
                name:
                  '<span style="color: #eb230a; pointer-events: none">' +
                  'Destination not supported or no matching destination' +
                  '<span>',
                id: null,
                isDisabled: true,
              },
            ];
            this.autoCompleteStartResults.emit(results);
          } else {
            this.autoCompleteStartResults.emit(data.locations);
          }
        },
        error: () => {
          const results = [
            {
              name:
                '<span style="color: #eb230a; pointer-events: none">' +
                'Destination not supported or no matching destination' +
                '<span>',
              id: null,
              isDisabled: true,
            },
          ];

          this.autoCompleteStartResults.emit(results);
        },
      });
  }

  getTripFlights(body: {
    cityId: number;
    returnCityId: number;
    dates: any;
    airport: string;
  }): void {
    let params = new HttpParams()
      .set('key', this.packageId)
      .set('city', body.cityId)
      .set('returncity', body.returnCityId)
      .set('dates', body.dates);

    if (body.airport) {
      params = params.set('airport', body.airport);
    }

    this.webApiService
      .get('flights/suggested', params)
      .pipe(
        tap((res) => {
          this.tripFlights = null;
          if (res.status === 'success') {
            this.tripFlights = res.results.map((flight) => {
              return {
                inbound: flight.inbound[0],
                outbound: flight.outbound[0],
                price: flight.price,
                link: flight.link,
              };
            });

            this.tripFlightsSubject.next(this.tripFlights);
          }
        }),
        finalize(() => (this.tripFlightsLoading = false)),
      )
      .subscribe();
  }

  public stopPollingByDestroy(): void {
    this.stopPolling = true;
  }

  private groupViatorEventsByCategories(eventsResp, prop: string): any {
    return eventsResp?.results?.reduce((acc, curr) => {
      if (curr && curr.category) {
        const categories = curr.category.split('|');
        for (let category of categories) {
          category = category.trim().toLowerCase();
          acc[category] = [
            ...(acc[category] ?? []),
            {
              id: curr.id,
              imageUrl: curr.image.url,
              url: curr.url,
              name: curr[prop],
              currency: curr.price.currency.sign,
              price: curr.price.visible,
            },
          ];
        }
      }
      return acc;
    }, {});
  }
}
